// Gemeinsame Geometrie und Mathe fuer Intro-Animation und Launcher.

export const N = 260 // Stützpunkte des Drahts

export const C = {
  blue: '#407BFF',
  green: '#43DBBD',
  yellow: '#FFB628',
  pink: '#FE79A7',
  coolWhite: '#F6F7F9',
  dark: '#353535',
  grey: '#B2B4B6',
  wire: '#9AA0AA',
  teal: '#008080'
}

// ---------- math ----------
export const clamp = (x, a = 0, b = 1) => Math.min(b, Math.max(a, x))
export const lerp = (a, b, t) => a + (b - a) * t
export const prog = (t, a, b) => clamp((t - a) / (b - a))
export const E = {
  outCubic: (t) => 1 - (1 - t) ** 3,
  inCubic: (t) => t * t * t,
  inOutCubic: (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2),
  outBack: (t, s = 1.7) => 1 + (s + 1) * (t - 1) ** 3 + s * (t - 1) ** 2,
  outExpo: (t) => (t >= 1 ? 1 : 1 - 2 ** (-10 * t))
}
// gedämpfte Feder 0 → 1 (τ in Sekunden)
export const spring = (tau, k = 7, w = 13) =>
  tau <= 0 ? 0 : 1 - Math.exp(-k * tau) * Math.cos(w * tau)
export const step = (t, fps = 12) => Math.floor(t * fps) / fps
export const rnd = (n) => {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453
  return x - Math.floor(x)
}
const hex = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16))
export const mixColor = (a, b, t) => {
  const A = hex(a)
  const B = hex(b)
  return `rgb(${A.map((v, i) => Math.round(lerp(v, B[i], t))).join(',')})`
}

// ---------- Draht-Geometrie ----------
// Segmente: ["L", x, y] | ["A", cx, cy, r, a0deg, a1deg]
export function buildShape(start, segs) {
  const pieces = []
  let [x, y] = start
  for (const s of segs) {
    if (s[0] === 'L') {
      const [, nx, ny] = s
      pieces.push({
        kind: 'L',
        x0: x,
        y0: y,
        x1: nx,
        y1: ny,
        len: Math.hypot(nx - x, ny - y)
      })
      x = nx
      y = ny
    } else {
      const [, cx, cy, r, a0, a1] = s
      const r0 = (a0 * Math.PI) / 180
      const r1 = (a1 * Math.PI) / 180
      pieces.push({ kind: 'A', cx, cy, r, r0, r1, len: Math.abs(r1 - r0) * r })
      x = cx + r * Math.cos(r1)
      y = cy + r * Math.sin(r1)
    }
  }
  const total = pieces.reduce((s, p) => s + p.len, 0)
  const pts = []
  for (let i = 0; i < N; i++) {
    let d = (i / (N - 1)) * total
    let p = pieces[0]
    for (const q of pieces) {
      p = q
      if (d <= q.len) break
      d -= q.len
    }
    const f = p.len ? clamp(d / p.len) : 0
    if (p.kind === 'L') pts.push([lerp(p.x0, p.x1, f), lerp(p.y0, p.y1, f)])
    else {
      const a = lerp(p.r0, p.r1, f)
      pts.push([p.cx + p.r * Math.cos(a), p.cy + p.r * Math.sin(a)])
    }
  }
  return pts
}

// Klassische Gem-Clip-Büroklammer (lokal, y nach unten)
export const RETRO = buildShape(
  [-30, -60],
  [
    ['L', -30, 100],
    ['A', 0, 100, 30, 180, 0],
    ['L', 30, -100],
    ['A', 6, -100, 24, 0, -180],
    ['L', -18, 60],
    ['A', -2, 60, 16, 180, 0],
    ['L', 14, -40]
  ]
)
// Moderne Klammer: zwei Bögen, dicke Monoline
export const MODERN = buildShape(
  [-22, 30],
  [
    ['L', -22, -40],
    ['A', 8, -40, 30, 180, 360],
    ['L', 38, 55],
    ['A', -3, 55, 41, 0, 180],
    ['L', -44, -22]
  ]
)

export const xf = (pts, { x, y, s, r = 0 }) => {
  const c = Math.cos(r)
  const sn = Math.sin(r)
  return pts.map(([px, py]) => [
    x + s * (px * c - py * sn),
    y + s * (px * sn + py * c)
  ])
}
export const toPath = (pts) => {
  let d = `M${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`
  for (let i = 1; i < pts.length; i++)
    d += `L${pts[i][0].toFixed(1)} ${pts[i][1].toFixed(1)}`
  return d
}
